import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import {
  useCancelJob,
  useCompleteJob,
  useRescheduleJob,
  useSetAssignments,
  useUpdateJob,
} from '@/hooks/jobs';
import { useServiceTypes } from '@/hooks/catalog-admin';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { AssignmentPicker } from '@/pages/admin/jobs/AssignmentPicker';
import type { JobDetail } from '@shared/schemas/job';

/*
 * The actions a job takes after it exists — ROADMAP.md §4.4 and §4.5.
 *
 * Each is its own small dialog rather than one editable form, because each is
 * its own operation on the server: a reschedule is audit-logged as a
 * reschedule, a cancellation demands a reason, a completion can be backdated.
 * The dialog boundary is the operation boundary.
 */

function ActionDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <Dialog.Title className="text-h2 text-ink-900">{title}</Dialog.Title>
          <Dialog.Description className="mt-1 text-body text-ink-500">
            {description}
          </Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DialogButtons({ isPending, submitLabel }: { isPending: boolean; submitLabel: string }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <Dialog.Close asChild>
        <Button type="button" variant="outline">
          Keep as it is
        </Button>
      </Dialog.Close>
      <Button type="submit" disabled={isPending}>
        {isPending ? 'Working…' : submitLabel}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reassign
// ---------------------------------------------------------------------------

export function AssignmentDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [employeeIds, setEmployeeIds] = useState<string[]>(
    job.assignees.map((entry) => entry.employeeId),
  );
  const mutation = useSetAssignments(job.id);
  const error = mutation.error instanceof ApiError ? mutation.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate(employeeIds, {
      onSuccess: () => {
        toast.success('Crew updated.');
        onOpenChange(false);
      },
    });
  }

  return (
    <ActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Who is on this job"
      description="The first person is the lead. Warnings show, and a daily limit blocks."
    >
      <form onSubmit={submit} className="mt-5 space-y-4">
        {error ? <FormError>{error.message}</FormError> : null}

        <AssignmentPicker
          params={{
            date: job.scheduledDate,
            time: job.scheduledTime,
            durationMinutes: job.estimatedDurationMinutes,
            excludeJobId: job.id,
            // The job already knows both, so the ranking here is always fully
            // scored — unlike the create dialog, where they arrive as the form
            // is filled in.
            clientId: job.clientId,
            serviceTypeId: job.serviceTypeId,
            // Nullable on a job whose property was retired after booking — the
            // address is snapshotted, so the job survives it. Distance simply
            // is not scored then, and the ranking says so.
            ...(job.propertyId === null ? {} : { propertyId: job.propertyId }),
          }}
          value={employeeIds}
          onChange={setEmployeeIds}
        />

        {job.status !== 'pending' && employeeIds.length === 0 ? (
          <p className="text-meta text-ink-700">
            A scheduled job needs at least one person on it. To take everyone off, cancel the job
            instead.
          </p>
        ) : null}

        <DialogButtons isPending={mutation.isPending} submitLabel="Save crew" />
      </form>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------------------
// Reschedule
// ---------------------------------------------------------------------------

/**
 * What a reschedule needs to know — deliberately less than a full JobDetail,
 * so the calendar can open this straight from a list item (4.7's keyboard
 * path to the same move a drag makes).
 */
export interface ReschedulableJob {
  id: string;
  jobNumber: string;
  scheduledDate: string;
  scheduledTime: string;
}

export function RescheduleDialog({
  job,
  open,
  onOpenChange,
}: {
  job: ReschedulableJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [scheduledDate, setScheduledDate] = useState(job.scheduledDate);
  const [scheduledTime, setScheduledTime] = useState(job.scheduledTime.slice(0, 5));
  const mutation = useRescheduleJob(job.id);
  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate(
      { scheduledDate, scheduledTime },
      {
        onSuccess: () => {
          toast.success('Rescheduled.');
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <ActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Reschedule ${job.jobNumber}`}
      description="The crew stays on the job — check their warnings if the day changes."
    >
      <form onSubmit={submit} className="mt-5 space-y-4">
        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Date"
            name="scheduledDate"
            type="date"
            required
            value={scheduledDate}
            error={fieldErrors.scheduledDate}
            onChange={(event) => {
              setScheduledDate(event.target.value);
            }}
          />
          <Field
            label="Start time"
            name="scheduledTime"
            type="time"
            required
            value={scheduledTime}
            error={fieldErrors.scheduledTime}
            onChange={(event) => {
              setScheduledTime(event.target.value);
            }}
          />
        </div>

        <DialogButtons isPending={mutation.isPending} submitLabel="Reschedule" />
      </form>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------------------
// Cancel — reason required, and it is terminal
// ---------------------------------------------------------------------------

export function CancelJobDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const mutation = useCancelJob(job.id);
  const error = mutation.error instanceof ApiError ? mutation.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate(reason, {
      onSuccess: () => {
        toast.success(`${job.jobNumber} cancelled.`);
        onOpenChange(false);
      },
    });
  }

  return (
    <ActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Cancel ${job.jobNumber}?`}
      description="Cancelling is permanent — a cancelled job stays on the record and cannot be brought back. If the visit is just moving, reschedule it instead."
    >
      <form onSubmit={submit} className="mt-5 space-y-4">
        {error ? <FormError>{error.message}</FormError> : null}

        <div className="space-y-1.5">
          <Label htmlFor="cancel-reason" required>
            Why it is being cancelled
          </Label>
          <textarea
            id="cancel-reason"
            name="reason"
            rows={3}
            required
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            placeholder="Client called it off, weather, double-booked…"
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body placeholder:text-ink-500"
          />
          <p className="text-meta text-ink-500">Kept with the job’s history.</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Dialog.Close asChild>
            <Button type="button" variant="outline">
              Keep the job
            </Button>
          </Dialog.Close>
          <Button type="submit" variant="danger" disabled={mutation.isPending}>
            {mutation.isPending ? 'Cancelling…' : 'Cancel the job'}
          </Button>
        </div>
      </form>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------------------
// Complete — with the backdating seam
// ---------------------------------------------------------------------------

export function CompleteJobDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [backdate, setBackdate] = useState(false);
  const [completedAt, setCompletedAt] = useState('');
  const mutation = useCompleteJob(job.id);
  const error = mutation.error instanceof ApiError ? mutation.error : null;

  const openRequired = job.taskCounts.requiredTotal - job.taskCounts.requiredCompleted;

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate(backdate && completedAt !== '' ? completedAt : undefined, {
      onSuccess: () => {
        toast.success(`${job.jobNumber} completed.`);
        onOpenChange(false);
      },
    });
  }

  return (
    <ActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Complete ${job.jobNumber}`}
      description={
        openRequired > 0
          ? `${String(openRequired)} required task${openRequired === 1 ? ' is' : 's are'} still open — completing will be refused until every required task is done, photo-required ones by their photo.`
          : 'Every required task is done.'
      }
    >
      <form onSubmit={submit} className="mt-5 space-y-4">
        {error ? <FormError>{error.message}</FormError> : null}

        <label className="flex min-h-touch items-center gap-2.5">
          <input
            type="checkbox"
            name="backdate"
            checked={backdate}
            onChange={(event) => {
              setBackdate(event.target.checked);
            }}
            className="size-4 shrink-0 accent-honey-500"
          />
          <span className="text-body text-ink-900">The work finished earlier — record when</span>
        </label>

        {backdate ? (
          <Field
            label="Finished at"
            name="completedAt"
            type="datetime-local"
            required
            value={completedAt}
            hint="Cedar City time. Backdating is kept in the audit trail."
            onChange={(event) => {
              setCompletedAt(event.target.value);
            }}
          />
        ) : null}

        <DialogButtons isPending={mutation.isPending} submitLabel="Complete the job" />
      </form>
    </ActionDialog>
  );
}

// ---------------------------------------------------------------------------
// Edit — the fields that are an edit, not an operation
// ---------------------------------------------------------------------------

export function EditJobDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [serviceTypeId, setServiceTypeId] = useState(job.serviceTypeId);
  const [duration, setDuration] = useState(String(job.estimatedDurationMinutes));
  const [specialInstructions, setSpecialInstructions] = useState(job.specialInstructions ?? '');
  const [isHighPriority, setIsHighPriority] = useState(job.isHighPriority);

  const services = useServiceTypes({ page: 1, search: '', activity: 'active' });
  const mutation = useUpdateJob(job.id);
  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate(
      {
        serviceTypeId,
        estimatedDurationMinutes: Number(duration),
        specialInstructions,
        isHighPriority,
      },
      {
        onSuccess: () => {
          toast.success('Saved.');
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <ActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Edit ${job.jobNumber}`}
      description="The date moves through Reschedule, and the checklist stays as it was created."
    >
      <form onSubmit={submit} className="mt-5 space-y-4">
        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="edit-serviceTypeId" required>
              Service
            </Label>
            <select
              id="edit-serviceTypeId"
              name="serviceTypeId"
              value={serviceTypeId}
              onChange={(event) => {
                setServiceTypeId(event.target.value);
              }}
              className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              {(services.data?.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
              {/* The current service even if it has since been retired. */}
              {(services.data?.data ?? []).every((entry) => entry.id !== job.serviceTypeId) ? (
                <option value={job.serviceTypeId}>{job.serviceTypeName}</option>
              ) : null}
            </select>
          </div>

          <Field
            label="Duration (minutes)"
            name="estimatedDurationMinutes"
            type="number"
            min={15}
            max={1440}
            step={15}
            required
            value={duration}
            error={fieldErrors.estimatedDurationMinutes}
            onChange={(event) => {
              setDuration(event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="edit-specialInstructions">Special instructions</Label>
          <textarea
            id="edit-specialInstructions"
            name="specialInstructions"
            rows={3}
            value={specialInstructions}
            onChange={(event) => {
              setSpecialInstructions(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          />
        </div>

        <label className="flex min-h-touch items-center gap-2.5">
          <input
            type="checkbox"
            name="isHighPriority"
            checked={isHighPriority}
            onChange={(event) => {
              setIsHighPriority(event.target.checked);
            }}
            className="size-4 shrink-0 accent-honey-500"
          />
          <span className="text-body text-ink-900">High priority</span>
        </label>

        <DialogButtons isPending={mutation.isPending} submitLabel="Save" />
      </form>
    </ActionDialog>
  );
}
